import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn, LogOut, LayoutDashboard } from "lucide-react";

const GoogleG = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.3 5.2C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

const Header = () => {
  const { t, lang, toggle } = useLang();
  const { user, login, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "gallery", label: t("nav.gallery") },
    { id: "exhibition", label: t("nav.exhibition") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/5 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <button
          data-testid="brand-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Home"
          className="w-6 h-6"
        />


        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="overline text-[#a3a09a] hover:text-[#f5f2eb] link-underline transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="overline text-[#f5f2eb] border border-white/15 rounded-full px-3 py-1 hover:border-[#b02418] hover:text-[#d97925] transition-colors"
          >
            {lang === "sk" ? "EN" : "SK"}
          </button>
          {user ? (
            <div className="flex items-center gap-3">
              {user.is_admin && (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className="overline text-[#d97925] hover:text-[#f5f2eb] transition-colors flex items-center gap-1.5"
                >
                  <LayoutDashboard size={15} /> Admin
                </Link>
              )}
              {user.picture ? (
                <img src={user.picture} alt={user.name || user.email} className="w-8 h-8 rounded-full border border-white/15" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-[#d97925]/20 border border-white/15 flex items-center justify-center text-xs text-[#d97925]">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </span>
              )}
              <button
                data-testid="logout-btn"
                onClick={logout}
                title="Odhlásiť"
                className="text-[#a3a09a] hover:text-[#f5f2eb] transition-colors"
              >
                <LogOut size={17} />
              </button>
            </div>
          ) : (
            <button
              data-testid="google-login-btn"
              onClick={login}
              className="overline flex items-center gap-2 bg-white text-[#1a1a1a] rounded-full pl-2.5 pr-4 py-1.5 hover:bg-[#f5f2eb] transition-colors"
            >
              <GoogleG /> {lang === "sk" ? "Prihlásiť" : "Sign in"}
            </button>
          )}
        </nav>

        <div className="md:hidden flex items-center gap-4">
          <button data-testid="lang-toggle-mobile" onClick={toggle} className="overline text-[#f5f2eb]">
            {lang === "sk" ? "EN" : "SK"}
          </button>
          <button data-testid="menu-toggle" onClick={() => setOpen((o) => !o)} className="text-[#f5f2eb]">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="overline text-left text-[#a3a09a] hover:text-[#f5f2eb]"
                >
                  {l.label}
                </button>
              ))}
              {user ? (
                <>
                  {user.is_admin && (
                    <Link to="/admin" data-testid="admin-link-mobile" onClick={() => setOpen(false)} className="overline text-left text-[#d97925] flex items-center gap-2">
                      <LayoutDashboard size={15} /> Admin
                    </Link>
                  )}
                  <button data-testid="logout-btn-mobile" onClick={() => { setOpen(false); logout(); }} className="overline text-left text-[#a3a09a] flex items-center gap-2">
                    <LogOut size={15} /> {lang === "sk" ? "Odhlásiť" : "Sign out"}
                  </button>
                </>
              ) : (
                <button data-testid="google-login-btn-mobile" onClick={() => { setOpen(false); login(); }} className="overline text-left text-[#f5f2eb] flex items-center gap-2">
                  <GoogleG /> {lang === "sk" ? "Prihlásiť sa cez Google" : "Sign in with Google"}
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
